import { ReceiptText } from 'lucide-react'
import { Link } from 'react-router'

import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import { EetSubmission } from '@invo.space/shared'
import { Receipt } from '@invo.space/shared'
import { Role } from '@/types/User'

import { ReceiptItem } from './ReceiptItem'

interface ReceiptsProps {
  receipts: Receipt[]
  isLoading?: boolean
  eetByReceiptId?: Record<string, EetSubmission>
  /** Sloupec EET 2.0 jen když se účtenky do EET 2.0 posílají */
  showEet?: boolean
  onSelect?: (receipt: Receipt) => void
  /** Je to prázdné kvůli filtru, ne kvůli dni bez tržeb */
  filtered?: boolean
  footer?: React.ReactNode
}

/** Tabulka účtenek ve stejném jazyce jako seznam dokladů. */
export const Receipts = ({ receipts, isLoading, eetByReceiptId, showEet, onSelect, filtered, footer }: ReceiptsProps) => {
  const canCreateReceipt = useRoleCheck([Role.owner, Role.manager, Role.employee])
  const columns = showEet ? 6 : 5

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="hidden w-44 md:table-cell">Číslo</TableHead>
            <TableHead>Položky</TableHead>
            <TableHead className="hidden w-32 md:table-cell">Platba</TableHead>
            {showEet && <TableHead className="hidden w-32 md:table-cell">EET 2.0</TableHead>}
            <TableHead className="text-right md:w-36">Částka</TableHead>
            <TableHead className="hidden w-10 md:table-cell" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading &&
            Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: columns }).map((__, j) => (
                  <TableCell key={j} className={j === 1 || j === columns - 2 ? undefined : 'hidden md:table-cell'}>
                    <Skeleton className="h-4 w-full max-w-32" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {!isLoading &&
            receipts.map((receipt) => (
              <ReceiptItem
                key={receipt.id}
                receipt={receipt}
                eetSubmission={eetByReceiptId?.[receipt.id]}
                showEet={showEet}
                onSelect={onSelect}
              />
            ))}
        </TableBody>
      </Table>

      {!isLoading && receipts.length === 0 && (
        <div className="p-6">
          <EmptyState icon={ReceiptText}
            title={filtered ? 'Žádná účtenka neodpovídá filtru' : 'V tento den nebyla vystavena žádná účtenka'}
            description={filtered ? 'Zkuste jiný způsob platby nebo hledaný výraz.' : undefined}
            ctaButton={
              !filtered && canCreateReceipt ? (
                <Button asChild>
                  <Link to="/cash-register">Otevřít pokladnu</Link>
                </Button>
              ) : undefined
            }
          />
        </div>
      )}

      {footer}
    </div>
  )
}
